import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLogger } from "../hooks/useLogger";
import { scrubUrl } from "../utils/scrubUrl";

// Every static segment the router declares is lower-case kebab-case (see Layout.tsx), so anything
// else is a dynamic value — a uuid or a class token like "VELMWX" — and collapses to ":id" to keep
// `page_name` low-cardinality. A hand-typed lower-case token slips through (react-router matches
// case-insensitively); accepted, since every app-generated link uses upper-case tokens. The login
// code is scrubbed first, as a letters-only code typed in lower case would otherwise end up here.
const _isIdSegment = (segment: string): boolean => !/^[a-z][a-z-]*$/.test(segment);

const _getPageName = (pathname: string): string => {
    const slug = scrubUrl(pathname)
        .split("/")
        .filter(Boolean)
        .map((segment) => (_isIdSegment(segment) ? ":id" : segment.replace(/-/g, "_")))
        .join("_");

    return slug || "root";
};

/*
 * Fires a single global "Page Viewed" event on every route change. This is the SPA
 * equivalent of an automatic pageview (we listen to history ourselves, since CRA can't
 * autocapture). It is the "did the user arrive on this screen" signal for every funnel.
 *
 * logEvent drops events without analytics consent, and granting
 * consent always ends in a full page reload (cookie.service.ts), so the fresh mount after
 * the reload reports the page the user landed on. On a hard load the event fires before
 * user info resolves, so it reports user_role "other" — accepted trade-off, better than
 * risking a dropped landing event.
 */
export default function PageViewTracker() {
    const { pathname } = useLocation();
    const { logEvent } = useLogger();

    // Keep the latest logEvent in a ref so the effect fires once per route change, not
    // whenever logEvent's identity changes (e.g. when user/teacher info loads), which
    // would otherwise double-count page views for the same path.
    const logEventRef = useRef(logEvent);
    logEventRef.current = logEvent;

    useEffect(() => {
        // The `page_path` is attached to every event by useLogger, so no need to track it here
        // and page_name is the clean, low-cardinality label we build funnels on.
        logEventRef.current("Page Viewed", { page_name: _getPageName(pathname) });
    }, [pathname]);

    return null;
}
